import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Users, 
  UserPlus,
  Building2 
} from 'lucide-react';

/**
 * RegistrationTypeSelector Component
 * Allows users to select who they are registering for
 */
export const RegistrationTypeSelector = ({
  registrationType,
  setRegistrationType,
  variant,
  maxParticipants,
}) => {
  const isGroupVariant = variant?.toLowerCase().includes('group') || 
                         variant?.toLowerCase().includes('20 participants') ||
                         maxParticipants > 5;

  const registrationOptions = [
    {
      id: 'self',
      label: 'Register for Myself',
      description: 'I will be attending this webinar',
      icon: User,
      available: !isGroupVariant,
    },
    {
      id: 'others',
      label: 'Register for Others',
      description: 'I am booking for someone else (they will receive the registration details)',
      icon: UserPlus,
      available: true,
    },
    {
      id: 'self_and_others',
      label: 'Register for Myself & Others',
      description: 'I will attend along with other participants',
      icon: Users,
      available: maxParticipants > 1,
    },
  ];

  // For corporate/group variants, default to 'others' if 'self' is selected
  React.useEffect(() => {
    if (isGroupVariant && registrationType === 'self') {
      setRegistrationType('others');
    }
  }, [isGroupVariant, registrationType, setRegistrationType]);

  return (
    <Card className="border-slate-200 rounded-xl">
      <CardContent className="pt-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-navy-700 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Who is this registration for?
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Select whether you're registering for yourself, others, or both
          </p>
        </div>

        <RadioGroup
          value={registrationType}
          onValueChange={setRegistrationType}
          className="space-y-3"
        >
          {registrationOptions
            .filter((option) => option.available)
            .map((option) => {
              const Icon = option.icon;
              return (
                <div key={option.id}>
                  <Label
                    htmlFor={option.id}
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      registrationType === option.id
                        ? 'border-navy-500 bg-navy-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <RadioGroupItem value={option.id} id={option.id} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${
                          registrationType === option.id ? 'text-navy-600' : 'text-slate-500'
                        }`} />
                        <span className={`font-medium ${
                          registrationType === option.id ? 'text-navy-700' : 'text-slate-700'
                        }`}>
                          {option.label}
                        </span>
                        {option.id === 'others' && isGroupVariant && (
                          <Badge className="bg-gold-100 text-gold-700 text-xs">
                            Corporate/Group
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {option.description}
                      </p>
                    </div>
                  </Label>
                </div>
              );
            })}
        </RadioGroup>

        {isGroupVariant && (
          <div className="mt-4 p-3 bg-gold-50 rounded-lg border border-gold-200">
            <div className="flex items-start gap-2">
              <Building2 className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gold-800">
                  Corporate Group Registration
                </p>
                <p className="text-xs text-gold-700 mt-1">
                  You can register up to {maxParticipants} participants. Each participant will 
                  receive their own registration confirmation email with access details.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RegistrationTypeSelector;
